import { ProductSortMode } from 'types'

const norm = (v: unknown) =>
  String(v ?? '')
    .trim()
    .toLowerCase()

const getCategory = (p: any) => norm(p?.category_name ?? p?.category ?? p?.categoryName)

const getSubcategory = (p: any) => norm(p?.subcategory ?? '')

const getStartDate = (p: any): string => {
  // Try startDate first, then dateRange[0]
  const date = p?.startDate ?? p?.dateRange?.[0] ?? ''
  return String(date).trim()
}

const getPrice = (p: any): number => {
  const raw = p?.billedPrice ?? p?.price ?? 0
  const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ''))
  return isNaN(n) ? 0 : n
}

// Pinned subcategory order for DIGITAL_SUBCATEGORY_FIRST
const DIGITAL_SUB_RANK: Record<string, number> = { search: 0, social: 1, display: 2 }

// Pinned status order for STATUS_ACTIVE_FIRST — live work first, terminal last, blanks after that
const STATUS_RANK: Record<string, number> = {
  active: 0,
  'active (system)': 0,
  monthly: 1,
  pending: 2,
  '30 day notice': 3,
  ended: 4,
  'ended (system)': 4,
  expired: 5,
  'expired (system)': 5,
  cancelled: 6,
  canceled: 6,
}

const UNRANKED_STATUS = 99

const getStatusRank = (p: any): number => {
  const status = norm(p?.status)
  if (!status) return UNRANKED_STATUS + 1 // blanks always last, in both directions
  return STATUS_RANK[status] ?? UNRANKED_STATUS
}

export const sortProducts = (rows: any[], mode: ProductSortMode) => {
  const withIdx = rows.map((r, i) => ({ r, i })) // stable tie-breaker
  const cmp = (a: any, b: any) => (a < b ? -1 : a > b ? 1 : 0)

  withIdx.sort((A, B) => {
    const a = A.r
    const b = B.r

    const nameA = norm(a?.productName ?? a?.title ?? a?.name)
    const nameB = norm(b?.productName ?? b?.title ?? b?.name)

    const catA = getCategory(a)
    const catB = getCategory(b)

    const dateA = getStartDate(a)
    const dateB = getStartDate(b)

    const subA = getSubcategory(a)
    const subB = getSubcategory(b)

    let res = 0

    switch (mode) {
      case 'SAVED_ORDER':
        // Products arrive pre-sorted by sortOrder from useDealData — return unchanged.
        res = 0
        break
      case 'NAME_CATEGORY_ASC':
        res = cmp(nameA, nameB) || cmp(catA, catB)
        break
      case 'CATEGORY_NAME_ASC':
        res = cmp(catA, catB) || cmp(nameA, nameB)
        break
      case 'NAME_ASC':
        res = cmp(nameA, nameB)
        break
      case 'NAME_DESC':
        res = cmp(nameB, nameA)
        break
      case 'DATE_ASC':
        res = cmp(dateA, dateB) || cmp(nameA, nameB)
        break
      case 'DATE_DESC':
        res = cmp(dateB, dateA) || cmp(nameA, nameB)
        break
      case 'CATEGORY_ASC':
        res = cmp(catA, catB) || cmp(subA, subB) || cmp(nameA, nameB)
        break
      case 'CATEGORY_DESC':
        res = cmp(catB, catA) || cmp(subB, subA) || cmp(nameA, nameB)
        break
      case 'SUBCATEGORY_ASC':
        res = cmp(subA, subB) || cmp(catA, catB) || cmp(nameA, nameB)
        break
      case 'SUBCATEGORY_DESC':
        res = cmp(subB, subA) || cmp(catB, catA) || cmp(nameA, nameB)
        break
      case 'STATUS_ACTIVE_FIRST':
      case 'STATUS_ACTIVE_LAST': {
        const rankA = getStatusRank(a)
        const rankB = getStatusRank(b)
        const blankA = rankA > UNRANKED_STATUS ? 1 : 0
        const blankB = rankB > UNRANKED_STATUS ? 1 : 0
        // Blank statuses stay at the bottom regardless of direction
        res =
          cmp(blankA, blankB) ||
          (mode === 'STATUS_ACTIVE_FIRST' ? cmp(rankA, rankB) : cmp(rankB, rankA)) ||
          cmp(nameA, nameB)
        break
      }
      case 'PRICE_ASC':
        res = cmp(getPrice(a), getPrice(b)) || cmp(nameA, nameB)
        break
      case 'PRICE_DESC':
        res = cmp(getPrice(b), getPrice(a)) || cmp(nameA, nameB)
        break
      case 'DIGITAL_SUBCATEGORY_FIRST': {
        const rankA = DIGITAL_SUB_RANK[subA] ?? 99
        const rankB = DIGITAL_SUB_RANK[subB] ?? 99
        res = cmp(rankA, rankB) || cmp(subA, subB) || cmp(nameA, nameB)
        break
      }
    }

    // stable fallback (keeps original relative order for ties)
    return res || A.i - B.i
  })

  return withIdx.map(x => x.r)
}
